import { cn } from '@lib/utils';
import { formatPercentage } from '@utils/number.util';
import React, { useState } from 'react';
import { CircularProgress } from './circular-progressbar.component';

type SlotData = {
  isComplete: boolean;
  label?: string | number;
};

type SurveyHeaderProps = {
  progress: number;
  header: string | React.ReactNode;
  title: string;
  value: string | number;
  slots?: SlotData[];
  currentQuestionIndex?: number;
  onSlotClick?: (index: number) => void;
};

export const SurveyHeader: React.FC<SurveyHeaderProps> = (props) => {
  const { progress, header, title, slots = [], currentQuestionIndex = -1, onSlotClick } = props;

  const normalizedSlots = slots.map((slot) => (typeof slot === 'boolean' ? { isComplete: slot } : slot));
  const [hoveredSlot, setHoveredSlot] = useState<number | null>(null);

  const handleSlotClick = (index: number) => {
    if (onSlotClick) {
      onSlotClick(index);
    }
  };

  return (
    <div className="@container/survey-header overflow-hidden rounded-xl">
      <div className="flex flex-col px-6 py-6 bg-white gap-4">
        <div className="flex flex-col gap-4 @min-[28rem]/survey-header:flex-row @min-[28rem]/survey-header:items-center @min-[28rem]/survey-header:justify-between">
          <div className="flex flex-col gap-1.5">
            <div className="font-semibold text-sm text-[#3d2eff]">{header}</div>
            <div className="flex flex-col gap-1.5">
              <div className="text-stone-900 text-lg font-medium">{title}</div>
            </div>
          </div>
          <CircularProgress
            percentage={progress}
            value={formatPercentage(progress)}
          />
        </div>
      </div>

      {slots.length > 1 && (
        <div className="relative flex mt-[-28px] mx-3 h-6">
          <div className={cn('flex w-full', slots.length >= 20 ? 'gap-1' : 'gap-2')}>
            {normalizedSlots.map((slot, index) => {
              const isComplete = slot.isComplete;
              const label = slot.label;
              const isInteractive = Boolean(onSlotClick) && Boolean(label);
              const isCurrent = index === currentQuestionIndex;

              return (
                <div
                  key={index}
                  className={cn('relative flex-1 transition-all duration-200', isInteractive && 'cursor-pointer')}
                  onMouseEnter={() => isInteractive && setHoveredSlot(index)}
                  onMouseLeave={() => isInteractive && setHoveredSlot(null)}
                  onClick={() => isInteractive && handleSlotClick(index)}
                  role={isInteractive ? 'button' : undefined}
                  aria-label={isInteractive ? `Slot ${label}${isComplete ? ' (completed)' : ''}` : undefined}
                >
                  {/* {isCurrent && (
                    <div className="absolute top-[8px] left-1/2 transform -translate-x-1/2 size-2 bg-green-500 rounded-full"></div>
                  )} */}

                  <div
                    className={cn(
                      'absolute bottom-0 left-0 right-0 h-1',
                      isCurrent ? 'bg-green-500' : isComplete ? 'bg-[#3d2eff]' : 'bg-[#eceaff]',
                    )}
                  />

                  {isInteractive && hoveredSlot === index && (
                    <div className="absolute bottom-0 left-0 right-0 h-[15px] bg-[#3d2eff] rounded-t-md z-10">
                      {label && (
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 bg-white text-xs font-medium rounded shadow">
                          {label}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
